import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Info, AlertCircle, CheckCircle2, AlertTriangle, MessageSquare, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { renderWithMentions } from "./MentionTextarea";
import { API } from "@/lib/api";

export function resolveAttachmentUrl(rawSrc) {
  if (!rawSrc) return "";
  const token = localStorage.getItem("ft_token") || "";
  let src = rawSrc;

  // Normalize /api/ or api/ prefixes to use API constant
  if (src.startsWith("/api/")) {
    src = `${API}${src.substring(4)}`;
  } else if (src.startsWith("api/")) {
    src = `${API}${src.substring(3)}`;
  }

  // Ensure current user's auth token is attached/updated for /attachments/.../download URLs
  if (src.includes("/attachments/") && src.includes("/download")) {
    try {
      const urlObj = new URL(src, window.location.origin);
      if (token) {
        urlObj.searchParams.set("auth", token);
      }
      src = urlObj.toString();
    } catch {
      if (token) {
        if (src.includes("auth=")) {
          src = src.replace(/auth=[^&]+/, `auth=${encodeURIComponent(token)}`);
        } else {
          src += (src.includes("?") ? "&" : "?") + `auth=${encodeURIComponent(token)}`;
        }
      }
    }
  }

  return src;
}

export function CalloutBox({ type = "info", children }) {
  const meta = {
    info: { icon: Info, cls: "bg-blue-500/10 border-blue-500/30 text-blue-900 dark:text-blue-200 icon-blue-500" },
    warning: { icon: AlertCircle, cls: "bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200 icon-amber-500" },
    success: { icon: CheckCircle2, cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200 icon-emerald-500" },
    note: { icon: MessageSquare, cls: "bg-slate-500/10 border-slate-500/30 text-slate-900 dark:text-slate-200 icon-slate-500" },
    danger: { icon: AlertTriangle, cls: "bg-red-500/10 border-red-500/30 text-red-900 dark:text-red-200 icon-red-500" },
  }[type] || { icon: Info, cls: "bg-muted border-border text-foreground" };

  const Icon = meta.icon;

  return (
    <div className={cn("flex gap-3 p-3 my-3 rounded-lg border text-xs leading-relaxed font-medium shadow-sm", meta.cls)}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

export function CodeBlock({ children, className }) {
  const [copied, setCopied] = useState(false);
  const lang = (className || "").replace("language-", "");

  const copyCode = () => {
    navigator.clipboard.writeText(String(children || ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-3 rounded-lg border border-border bg-slate-950 text-slate-50 overflow-hidden font-mono text-xs shadow-md">
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-900 border-b border-slate-800 text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
        <span>{lang || "code"}</span>
        <button type="button" onClick={copyCode} className="hover:text-white transition-colors">
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-3 overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function IssueKeyLink({ k }) {
  return (
    <span className="inline-flex items-center gap-0.5 font-mono text-xs font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20 hover:bg-primary/20 transition-colors">
      {k}
    </span>
  );
}

function MentionBadge({ name, users }) {
  const cleanName = (name || "").replace(/^@/, "");
  const matchUser = (users || []).find(
    (u) =>
      (u.name || "").replace(/\s+/g, "").toLowerCase() === cleanName.toLowerCase() ||
      (u.email || "").split("@")[0].toLowerCase() === cleanName.toLowerCase() ||
      (u.name || "").toLowerCase() === cleanName.toLowerCase()
  );

  return (
    <span
      className="inline-flex items-center gap-0.5 font-semibold text-xs text-primary bg-primary/10 hover:bg-primary/20 border border-primary/20 rounded px-1.5 py-0.5 mx-0.5 transition-colors align-baseline cursor-pointer"
      title={matchUser ? `${matchUser.name} (${matchUser.email})` : `@${cleanName}`}
    >
      <span className="text-[10px] opacity-70 font-mono">@</span>
      <span>{matchUser ? matchUser.name : cleanName}</span>
    </span>
  );
}

export const createMarkdownComponents = (users = [], onImageClick = null) => ({
  h1: (props) => {
    const text = String(props.children || "");
    const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return <h1 id={id} className="font-display text-xl font-bold tracking-tight mt-5 mb-2 border-b pb-1 text-foreground" {...props} />;
  },
  h2: (props) => {
    const text = String(props.children || "");
    const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return <h2 id={id} className="font-display text-lg font-semibold tracking-tight mt-4 mb-2 text-foreground" {...props} />;
  },
  h3: (props) => {
    const text = String(props.children || "");
    const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return <h3 id={id} className="font-display text-base font-medium tracking-tight mt-3 mb-1.5 text-foreground" {...props} />;
  },
  p: (props) => {
    const text = String(props.children || "");
    if (text.startsWith(":::info")) return <CalloutBox type="info">{text.replace(/^:::info\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::warning")) return <CalloutBox type="warning">{text.replace(/^:::warning\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::success")) return <CalloutBox type="success">{text.replace(/^:::success\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::note")) return <CalloutBox type="note">{text.replace(/^:::note\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::danger")) return <CalloutBox type="danger">{text.replace(/^:::danger\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    return <p className="text-sm leading-relaxed my-2 text-foreground/90" {...props} />;
  },
  code: ({ inline, className, children, ...rest }) => {
    if (inline) {
      return <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded border border-border text-foreground" {...rest}>{children}</code>;
    }
    return <CodeBlock className={className}>{children}</CodeBlock>;
  },
  img: (props) => {
    const src = resolveAttachmentUrl(props.src);
    return (
      <span className="block my-3">
        <span className="relative inline-block max-w-full group">
          <img
            src={src}
            alt={props.alt || "Image"}
            onClick={() => onImageClick?.(src, props.alt)}
            className="w-auto max-w-full max-h-[600px] h-auto rounded-lg border border-border shadow-sm object-contain transition-transform hover:scale-[1.005] cursor-zoom-in bg-background/50 block"
          />
          <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 backdrop-blur-xs text-white text-[10px] px-2 py-0.5 rounded flex items-center gap-1 pointer-events-none shadow-xs">
            Click to expand
          </span>
        </span>
        {props.alt && props.alt !== "Description Image" && props.alt !== "Image" && (
          <span className="block text-xs text-muted-foreground mt-1 font-medium">{props.alt}</span>
        )}
      </span>
    );
  },
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-border shadow-xs bg-card">
      <table className="w-full text-sm border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="bg-muted/70 border-b border-border" {...props} />
  ),
  tbody: ({ node, ...props }) => (
    <tbody className="divide-y divide-border/60 bg-card" {...props} />
  ),
  tr: ({ node, ...props }) => (
    <tr className="transition-colors hover:bg-muted/30" {...props} />
  ),
  th: ({ node, children, ...props }) => (
    <th
      className="px-4 py-2.5 text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground border-r last:border-r-0 border-border/50 min-w-[80px]"
      {...props}
    >
      {children || <span className="text-muted-foreground/40 italic">—</span>}
    </th>
  ),
  td: ({ node, children, ...props }) => (
    <td
      className="px-4 py-2.5 text-sm text-foreground/90 border-r last:border-r-0 border-border/40 align-top min-w-[80px]"
      {...props}
    >
      {children || <span className="text-muted-foreground/30 italic">—</span>}
    </td>
  ),
  blockquote: (props) => <blockquote className="border-l-4 border-primary/60 pl-3.5 py-1 text-muted-foreground italic my-3" {...props} />,
  a: (props) => <a className="text-primary underline font-medium hover:text-primary/80" target="_blank" rel="noreferrer" {...props} />,
});

export default function MarkdownRenderer({ content, users = [], className = "", onEdit = null }) {
  const [zoomImage, setZoomImage] = useState(null);

  if (!content) return null;

  // Pre-normalize HTML tables or rich text if saved as HTML
  let normalizedContent = content;
  if (typeof content === "string") {
    // If content has HTML table tags or HTML blocks, convert to markdown first
    if (content.includes("<table") || content.includes("<TABLE") || (content.trim().startsWith("<") && !content.includes("|"))) {
      try {
        const { htmlToMarkdown } = require("@/lib/markdownUtils");
        const converted = htmlToMarkdown(content);
        if (converted && converted.trim()) {
          normalizedContent = converted;
        }
      } catch {
        // fallback
      }
    }
  }

  return (
    <div className={cn("prose prose-sm dark:prose-invert max-w-none text-foreground", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={createMarkdownComponents(users, (src, alt) => setZoomImage({ src, alt }))}>
        {normalizedContent}
      </ReactMarkdown>

      {zoomImage && (
        <Dialog open={!!zoomImage} onOpenChange={() => setZoomImage(null)}>
          <DialogContent className="max-w-[95vw] w-auto p-4 bg-background/95 backdrop-blur-md border border-border shadow-2xl overflow-hidden flex flex-col items-center justify-center max-h-[95vh]">
            <div className="flex items-center justify-between w-full pb-2 border-b border-border/60 text-xs text-muted-foreground gap-4">
              <span className="font-medium truncate max-w-md">{zoomImage.alt || "Image Preview"}</span>
              <div className="flex items-center gap-2 pr-6">
                <a
                  href={zoomImage.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-secondary hover:bg-secondary/80 text-foreground text-xs font-medium transition-colors"
                  title="Open full resolution in new tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Open Full Size</span>
                </a>
              </div>
            </div>
            <div className="p-2 overflow-auto max-h-[82vh] w-full flex items-center justify-center">
              <img
                src={zoomImage.src}
                alt={zoomImage.alt || "Zoom"}
                className="max-h-[80vh] w-auto max-w-full rounded-md object-contain border border-border shadow-md"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
